import { FC } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  AverageValueResponse,
  useGetAverageVisitorLifetimeValueQuery
} from 'app/apis/queryApi';
import { RootState } from 'app/store';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import { getSymbolFromCurrency } from 'features/currency/currencySymbolMapHelper';
import Loading from 'common/components/loading/loading';
import { NumberTile, TileData } from 'features/tiles/numberTile';
import Tile from 'features/tiles/tile';

const useAverageVisitorLifetimeValue = (
  startDate: string,
  endDate: string,
  currencyCode: string
) => {
  return useGetAverageVisitorLifetimeValueQuery({
    siteId: useSelector((state: RootState) => state.selectedSite.siteId),
    startDate,
    endDate,
    currencyCode
  });
};

const AverageVisitorLifetimeValueTile: FC = () => {
  const dateRange = useSelector(getDateRangeAsObjects);
  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const {
    data: averageVisitorLifetimeValueData,
    isLoading: averageVisitorLifetimeValueIsLoading,
    isFetching: averageVisitorLifetimeValueIsFetching
  } = useAverageVisitorLifetimeValue(
    moment(dateRange.startDate).format('YYYY-MM-DD'),
    moment(dateRange.endDate).format('YYYY-MM-DD'),
    selectedCurrencyCode
  );
  const today = moment();
  const {
    data: averageVisitorLifetimeValueYearlyData,
    isLoading: averageVisitorLifetimeValueYearlyIsLoading,
    isFetching: averageVisitorLifetimeValueYearlyIsFetching
  } = useAverageVisitorLifetimeValue(
    today.clone().subtract(1, 'year').startOf('day').format('YYYY-MM-DD'),
    today.format('YYYY-MM-DD'),
    selectedCurrencyCode
  );
  const calculatePercentageDifference = (
    currentValue: number,
    comparisonValue: number
  ): number => {
    if (comparisonValue === 0) {
      return 0;
    }
    return ((currentValue - comparisonValue) / comparisonValue) * 100;
  };

  const getDataForTile = (
    tileTitle: string,
    data: AverageValueResponse,
    metricData: AverageValueResponse,
    locales: string,
    formatOptions: Intl.NumberFormatOptions,
    currencySymbol: string,
    selectedCurrencyCode: string
  ): TileData => {
    const percentageDifference = calculatePercentageDifference(
      data.averageValue,
      metricData.averageValue
    );

    const averageValueDifference = data.averageValue - metricData.averageValue;

    return {
      label: tileTitle,
      count: data.averageValue || 0,
      metric: `${
        averageValueDifference > 0
          ? currencySymbol +
            averageValueDifference.toLocaleString(locales, formatOptions) +
            ' above'
          : averageValueDifference
              .toLocaleString(locales, formatOptions)
              .replace('-', currencySymbol) + ' below'
      } annual average (${currencySymbol}${metricData.averageValue.toLocaleString(
        locales,
        formatOptions
      )})`,
      format: Intl.NumberFormat(locales, formatOptions),
      currencyCode: selectedCurrencyCode,
      percentageDiff: percentageDifference
    };
  };
  const shouldRenderTile =
    !averageVisitorLifetimeValueIsLoading &&
    !averageVisitorLifetimeValueYearlyIsLoading &&
    !averageVisitorLifetimeValueIsFetching &&
    !averageVisitorLifetimeValueYearlyIsFetching &&
    averageVisitorLifetimeValueData &&
    averageVisitorLifetimeValueYearlyData;
  return (
    <Tile className="w-full">
      {shouldRenderTile ? (
        <NumberTile
          tileData={getDataForTile(
            'Lifetime Value (LTV)',
            averageVisitorLifetimeValueData,
            averageVisitorLifetimeValueYearlyData,
            'en-GB',
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            },
            getSymbolFromCurrency(selectedCurrencyCode),
            selectedCurrencyCode
          )}
        />
      ) : (
        <Loading />
      )}
    </Tile>
  );
};

export default AverageVisitorLifetimeValueTile;
